/* eslint-disable prefer-const */
import _ from 'lodash';
import { LamellaTexturedBeam } from './ModelElements/LamellaTexturedBeam';
import { WoodOutlinedColoredBeam } from './ModelElements/WoodOutlinedColoredBeam';
import { WoodOutlinedColoredBeamSegmented } from './ModelElements/WoodOutlinedColoredBeamSegmented';
import FiberText from './FiberText/FiberText';

function createLamellaData(Component, width, thickness, length, position, rotation, key, color, extraProps = null) {
  return {
    component: Component,
    scale: [width / 200, thickness / 10000, length / 5000],
    position,
    rotation,
    key,
    color,
    ...(extraProps || {})
  };
}

function getQualityText(index, beamBuild) {
  return beamBuild[index] || 'ni';
}

function getBeamSequence(bshlayer, beamBuild) {
  const numOfLamellas = Number(_.get(bshlayer, 'numOfLamellas', 0)) || 0;
  const resolvedBuild = Array.isArray(beamBuild) && beamBuild.length ? beamBuild : ['lamellas'];
  const order = resolvedBuild.map((layer) => Number(_.get(bshlayer, layer, 0)) || 0);

  const total = order.reduce((acc, val) => acc + val, 0);
  // If we don't have valid composition data, show a simple sequence so 3D still renders.
  if (total <= 0 || numOfLamellas <= 0) {
    return Array(numOfLamellas || 1).fill(0);
  }

  const sequence = [];
  let perLamella = Math.floor(numOfLamellas / total);
  let remainder = numOfLamellas % total;

  for (let i = 0; i < order.length; i++) {
    let count = order[i] * perLamella;
    if (remainder > 0) {
      count += 1;
      remainder -= 1;
    }
    for (let j = 0; j < count; j++) {
      sequence.push(i);
    }
  }

  return sequence;
}

function getColorFromString(index, colors) {
  if (!Array.isArray(colors) || colors.length === 0) {
    return '808080'; // default gray without leading #
  }
  const color = colors[index % colors.length] || '#808080';
  return color.substring(1); // Return color without #
}

function pickPositiveNumber(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
}

export default function getBeamModel(order, layer, id, beamOptions) {
  const lamellas = [];
  const beamLayer = layer;
  const baseSpacingMultiplier = 12;

  /* Beam options from config */
  const renderMode = _.get(beamOptions, 'renderMode', 'legacy');
  const isLegacyLayout = renderMode === 'legacy';
  const useLamellaTexture = _.get(beamOptions, 'useLamellaTexture', false);
  const beamBuild = _.get(beamOptions, 'beamBuild', null);
  const beamBuildText = _.get(beamOptions, 'beamBuildText', []);
  const beamColors = _.get(beamOptions, 'beamColors', ['#808080']);
  const pendingSolidColorOverride = _.get(beamOptions, 'pendingSolidColor', null);
  const pendingOpacityOverride = _.get(beamOptions, 'pendingOpacity', null);
  const usePerLamellaPalette = _.get(beamOptions, 'beamColorMode', '') === 'perLamellaPalette' && Array.isArray(beamColors) && beamColors.length > 1;
  const useSegmentedBeamComponent = _.get(beamOptions, 'useSegmentedBeamComponent', false) || usePerLamellaPalette;
  const edgeScaleFactor = Number(_.get(beamOptions, 'edgeScaleFactor', 1.001));
  const edgeColor = _.get(beamOptions, 'edgeColor', '#111111');
  const edgeOpacity = Number(_.get(beamOptions, 'edgeOpacity', 0.92));
  const pendingSequence = Array.isArray(_.get(beamOptions, 'pendingSequence', null)) ? _.get(beamOptions, 'pendingSequence', null) : null;
  const textColors = _.get(beamOptions, 'textColors', ['#808080']);
  const useRotation = _.get(beamOptions, 'enableRotation', false);
  const useDynamicGap = _.get(beamOptions, 'enableDynamicGap', false);
  const useBeamColors = _.get(beamOptions, 'enableBeamColors', true);
  const renderQualityText = _.get(beamOptions, 'renderQualityText', true);
  const textXOffsetRaw = Number(_.get(beamOptions, 'textXOffset', 0));
  const textYOffsetRaw = Number(_.get(beamOptions, 'textYOffset', 0));
  const textZOffsetRaw = Number(_.get(beamOptions, 'textZOffset', 0));
  const textXStepRaw = Number(_.get(beamOptions, 'textXStep', 0));
  const textYStepRaw = Number(_.get(beamOptions, 'textYStep', 0));
  const textZStepRaw = Number(_.get(beamOptions, 'textZStep', 0));
  const textXOffset = Number.isFinite(textXOffsetRaw) ? textXOffsetRaw : 0;
  const textYOffset = Number.isFinite(textYOffsetRaw) ? textYOffsetRaw : 0;
  const textZOffset = Number.isFinite(textZOffsetRaw) ? textZOffsetRaw : 0;
  const textXStep = Number.isFinite(textXStepRaw) ? textXStepRaw : 0;
  const textYStep = Number.isFinite(textYStepRaw) ? textYStepRaw : 0;
  const textZStep = Number.isFinite(textZStepRaw) ? textZStepRaw : 0;
  const orderVerticalGapOverride = Number(_.get(beamOptions, 'orderVerticalGap', Number.NaN));
  // const gapMinRaw = Number(_.get(beamOptions, 'gapMin', 0.01));
  // const gapMaxRaw = Number(_.get(beamOptions, 'gapMax', 0.08));
  // const gapFactorRaw = Number(_.get(beamOptions, 'gapFactor', 0.6));
  const widthScaleDivRaw = Number(_.get(beamOptions, 'widthScaleDiv', isLegacyLayout ? 25.4 : 10));
  const widthScaleDiv = Number.isFinite(widthScaleDivRaw) && widthScaleDivRaw > 0 ? widthScaleDivRaw : 10;
  const thicknessScaleMultiplierRaw = Number(_.get(beamOptions, 'thicknessScaleMultiplier', isLegacyLayout ? 0.5 : 5));
  const thicknessScaleMultiplier = Number.isFinite(thicknessScaleMultiplierRaw) ? thicknessScaleMultiplierRaw : isLegacyLayout ? 0.5 : 5;
  const lengthScaleMultiplierRaw = Number(_.get(beamOptions, 'lengthScaleMultiplier', isLegacyLayout ? 1 : 5));
  const lengthScaleMultiplier = Number.isFinite(lengthScaleMultiplierRaw) ? lengthScaleMultiplierRaw : isLegacyLayout ? 1 : 5;
  const useNormalizedLayerSpacing = _.get(beamOptions, 'useNormalizedLayerSpacing', false);
  const orderWidthSpacingRaw = Number(_.get(beamOptions, 'orderWidthSpacing', baseSpacingMultiplier));
  const orderWidthSpacing = Number.isFinite(orderWidthSpacingRaw) ? orderWidthSpacingRaw : baseSpacingMultiplier;
  const stackDirection = _.get(beamOptions, 'stackDirection', 'vertical');
  const bundleSizeRaw = _.get(beamOptions, 'bundleSize', null);
  const bundleSize = Number.isFinite(Number(bundleSizeRaw)) && Number(bundleSizeRaw) > 0 ? Number(bundleSizeRaw) : null;
  const fixedDims = _.get(beamOptions, 'fixedDimensions', null);
  const fixedLamellaWidth = pickPositiveNumber(_.get(beamOptions, 'fixedLamellaWidth', null), fixedDims?.width, fixedDims?.lamellaWidth, fixedDims?.w);
  const fixedLamellaThickness = pickPositiveNumber(_.get(beamOptions, 'fixedLamellaThickness', null), fixedDims?.thickness, fixedDims?.lamellaThickness, fixedDims?.t, fixedDims?.h);
  const fixedLamellaLength = pickPositiveNumber(_.get(beamOptions, 'fixedLamellaLength', null), fixedDims?.length, fixedDims?.lamellaLength, fixedDims?.l);
  const useSolidLayout = _.get(beamOptions, 'solidLayout', false);
  const isHorizontal = stackDirection === 'horizontal';
  const BeamComponent = useLamellaTexture ? LamellaTexturedBeam : useSegmentedBeamComponent ? WoodOutlinedColoredBeamSegmented : WoodOutlinedColoredBeam;
  const finalizeLamellas = (items) => {
    if (!(usePerLamellaPalette || useSegmentedBeamComponent)) return items;
    return items.map((entry) => {
      if (entry?.component !== WoodOutlinedColoredBeamSegmented && entry?.component !== WoodOutlinedColoredBeam) return entry;
      return {
        ...entry,
        component: useSegmentedBeamComponent ? WoodOutlinedColoredBeamSegmented : entry.component,
        ...(usePerLamellaPalette ? { colorPalette: beamColors } : {}),
        edgeScaleFactor,
        edgeColor,
        edgeOpacity
      };
    });
  };

  // Early legacy path: mirror original Render3DScene behaviour for default profile.
  if (isLegacyLayout) {
    const legacyExtractWidths = (beamWidth) => (beamWidth || []).map((item) => Number(item) / 25.4);
    const legacyBeamBuild = Array.isArray(beamBuild) ? beamBuild : [];
    const legacySequence = (bshlayer) => {
      const numOfLamellas = Number(_.get(bshlayer, 'numOfLamellas', 0)) || 0;
      if (!legacyBeamBuild.length) {
        return Array(numOfLamellas || 1).fill(0);
      }
      const order = legacyBeamBuild.map((layerKey) => Number(_.get(bshlayer, layerKey, 0)) || 0);
      const total = order.reduce((acc, val) => acc + val, 0);
      if (total <= 0 || numOfLamellas <= 0) {
        return Array(numOfLamellas || 1).fill(0);
      }
      let perLamella = Math.floor(numOfLamellas / total);
      let remainder = numOfLamellas % total;
      const seq = [];
      for (let i = 0; i < order.length; i++) {
        let count = order[i] * perLamella;
        if (remainder > 0) {
          count += 1;
          remainder -= 1;
        }
        for (let j = 0; j < count; j++) {
          seq.push(i);
        }
      }
      return seq;
    };

    if (beamLayer) {
      const beamSequence = legacySequence(layer);
      const rawLayerWidths =
        Array.isArray(beamLayer.beamWidth) && beamLayer.beamWidth.length > 0
          ? beamLayer.beamWidth
          : Array.isArray(beamLayer.beamWidths) && beamLayer.beamWidths.length > 0
          ? beamLayer.beamWidths.map((w) => Number(w.lamellaWidth || w.rawLamellaWidth || 0))
          : Number.isFinite(Number(beamLayer.lamellaWidth)) && Number(beamLayer.lamellaWidth) > 0
          ? [Number(beamLayer.lamellaWidth)]
          : [];
      let lamellaBuild = legacyExtractWidths(rawLayerWidths);
      const totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);

      if (lamellaBuild.length > 0) {
        let previousText = null;
        let groupStartPosition = 0;
        const initialOffset = -totalWidth / 2;
        for (let j = 0; j < beamSequence.length; j++) {
          let offset = -totalWidth / 2;
          let reversed = (j + 1) % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
          for (let i = 0; i < reversed.length; i++) {
            offset += reversed[i];
            const currentText = getQualityText(beamSequence[j], beamBuildText);
            lamellas.push(
              createLamellaData(
                BeamComponent,
                reversed[i],
                beamLayer.lamellaThickness / 2,
                beamLayer.lamellaLength,
                [offset / 200, beamSequence.length ? j / beamSequence.length : 0, 0],
                0,
                `WoodOutlined_${j}_${i}`,
                getColorFromString(beamSequence[j], beamColors)
              )
            );
            const isLastItem = j === beamSequence.length - 1 && i === reversed.length - 1;
            if (renderQualityText && ((currentText !== previousText && previousText != null) || isLastItem)) {
              const groupEndPosition = isLastItem ? (j + 1) / beamSequence.length : (j - 1 + i) / beamSequence.length;
              const textPosition = (groupStartPosition + groupEndPosition) / 2;

              lamellas.push({
                component: FiberText,
                position: [initialOffset / 200 + 0.3, textPosition, 3],
                children: previousText,
                size: 0.6,
                key: `text-${j}-${i}`,
                color: `#${getBeamColorFromLamellaSequence(j - 1, getColorFromString(beamSequence[j - 1], beamColors), false)}`
              });

              groupStartPosition = beamSequence.length ? j / beamSequence.length : 0;
            }
            previousText = currentText;
            offset += reversed[i];
          }
        }
      }
      return finalizeLamellas(lamellas);
    }

    let k = 0;
    const spacingMultiplier = Number.isFinite(orderWidthSpacing) ? orderWidthSpacing : 12;

    (order?.layers || order?.bshLayers || []).forEach((olayer, b) => {
      const beamSequence = legacySequence(olayer);
      const rawWidths =
        Array.isArray(olayer.beamWidth) && olayer.beamWidth.length > 0
          ? olayer.beamWidth
          : Array.isArray(olayer.beamWidths) && olayer.beamWidths.length > 0
          ? olayer.beamWidths.map((w) => Number(w.lamellaWidth || w.rawLamellaWidth || 0))
          : Number.isFinite(Number(olayer.lamellaWidth)) && Number(olayer.lamellaWidth) > 0
          ? [Number(olayer.lamellaWidth)]
          : [];
      const lamellaBuild = legacyExtractWidths(rawWidths);
      const totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);
      const effectiveThickness = useSolidLayout && beamSequence.length > 0 ? 10000 / beamSequence.length : olayer.lamellaThickness / 2;

      let previousText = null;
      let groupStartPosition = 0;
      let offset = 0;
      const initialOffset = (offset = (-totalWidth / 2) * spacingMultiplier * (b + 1));

      for (let j = 0; j < beamSequence.length; j++) {
        let reversed = j % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
        offset = (-totalWidth / 2) * spacingMultiplier * (b + 1);
        for (let i = 0; i < reversed.length; i++) {
          offset += reversed[i];
          const currentText = getQualityText(beamSequence[j], beamBuildText);
          lamellas.push(
            createLamellaData(
              BeamComponent,
              reversed[i],
              effectiveThickness,
              olayer.lamellaLength,
              [offset / 200, beamSequence.length ? j / beamSequence.length : 0, 0],
              0,
              `Beam_${j}_${i}_${b}`,
              getColorFromString(beamSequence[j], beamColors)
            )
          );
          lamellas.push(
            createLamellaData(
              BeamComponent,
              reversed[i],
              effectiveThickness,
              2,
              [offset / 200, beamSequence.length ? j / beamSequence.length : olayer.lamellaLength / 4999],
              0,
              `Beam_${j}_${i + 100}_${b}`,
              getBeamColorFromLamellaSequence(k, getColorFromString(beamSequence[j], beamColors), true)
            )
          );

          const isLastItem = j === beamSequence.length - 1 && i === reversed.length - 1;
          if (renderQualityText && ((currentText !== previousText && previousText !== null) || isLastItem)) {
            const groupEndPosition = isLastItem ? (j + 1) / beamSequence.length : (j - 1 + i) / beamSequence.length;
            const textPosition = (groupStartPosition + groupEndPosition) / 2;
            const textColor = `#${getBeamColorFromLamellaSequence(k - 1, getColorFromString(beamSequence[j - 1], textColors), false)}`;

            lamellas.push({
              component: FiberText,
              position: [initialOffset / 200 + 0.3, textPosition, 3],
              children: previousText,
              size: 0.6,
              key: `text-${j}-${i}`,
              color: textColor
            });

            groupStartPosition = beamSequence.length ? j / beamSequence.length : 0;
          }

          previousText = currentText;
          k += 1;
          offset += reversed[i];
        }
      }
      // Handle the last group of lamellas
    });

    return finalizeLamellas(lamellas);
  }

  // Legacy behaviour mirrors the original Render3DScene implementation.
  function getLegacyBeamSequence(bshlayer, legacyBeamBuild) {
    const numOfLamellas = Number(_.get(bshlayer, 'numOfLamellas', 0)) || 0;
    if (!Array.isArray(legacyBeamBuild) || legacyBeamBuild.length === 0) {
      return Array(numOfLamellas || 1).fill(0);
    }

    const sequence = [];
    const order = legacyBeamBuild.map((legacyLayer) => {
      if (bshlayer[legacyLayer] === undefined) {
        return 0;
      }
      return bshlayer[legacyLayer];
    });

    const total = order.reduce((acc, val) => acc + val, 0);
    let perLamella = total > 0 ? Math.floor(numOfLamellas / total) : 0;
    let remainder = numOfLamellas % total;

    for (let i = 0; i < order.length; i++) {
      let count = order[i] * perLamella;
      if (remainder > 0) {
        count += 1;
        remainder -= 1;
      }
      for (let j = 0; j < count; j++) {
        sequence.push(i);
      }
    }

    return sequence;
  }

  if (isLegacyLayout) {
    const legacyExtractWidths = (beamWidth) => (beamWidth || []).map((item) => item / 25.4);

    if (beamLayer) {
      const beamSequence = getLegacyBeamSequence(layer, beamBuild);
      const lamellaBuild = legacyExtractWidths(beamLayer.beamWidth || []);
      const totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);

      if (lamellaBuild.length > 0) {
        let previousText = null;
        let groupStartPosition = 0;
        const initialOffset = -totalWidth / 2;
        for (let j = 0; j < beamSequence.length; j++) {
          let offset = -totalWidth / 2;
          let reversed = (j + 1) % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
          for (let i = 0; i < reversed.length; i++) {
            offset += reversed[i];
            const currentText = getQualityText(beamSequence[j], beamBuildText);
            lamellas.push(
              createLamellaData(
                BeamComponent,
                reversed[i],
                beamLayer.lamellaThickness / 2,
                beamLayer.lamellaLength,
                [offset / 200, j / beamSequence.length, 0],
                0,
                `WoodOutlined_${j}_${i}`,
                getColorFromString(beamSequence[j], beamColors)
              )
            );
            const isLastItem = j === beamSequence.length - 1 && i === reversed.length - 1;
            if (renderQualityText && ((currentText !== previousText && previousText != null) || isLastItem)) {
              const groupEndPosition = isLastItem ? (j + 1) / beamSequence.length : (j - 1 + i) / beamSequence.length;
              const textPosition = (groupStartPosition + groupEndPosition) / 2;

              lamellas.push({
                component: FiberText,
                position: [initialOffset / 200 + 0.3, textPosition, 3],
                children: previousText,
                size: 0.6,
                key: `text-${j}-${i}`,
                color: `#${getBeamColorFromLamellaSequence(j - 1, getColorFromString(beamSequence[j - 1], beamColors), false)}`
              });

              groupStartPosition = j / beamSequence.length;
            }
            previousText = currentText;
            offset += reversed[i];
          }
        }
      }
    } else {
      let k = 0;
      const spacingMultiplier = 12;

      order?.layers?.forEach((olayer, b) => {
        const beamSequence = getLegacyBeamSequence(olayer, beamBuild);
        const lamellaBuild = legacyExtractWidths(olayer.beamWidth || []);
        const totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);

        let previousText = null;
        let groupStartPosition = 0;
        let offset = 0;
        const initialOffset = (offset = (-totalWidth / 2) * spacingMultiplier * (b + 1));

        for (let j = 0; j < beamSequence.length; j++) {
          let reversed = j % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
          offset = (-totalWidth / 2) * spacingMultiplier * (b + 1);
          for (let i = 0; i < reversed.length; i++) {
            offset += reversed[i];
            const currentText = getQualityText(beamSequence[j], beamBuildText);
            lamellas.push(
              createLamellaData(
                BeamComponent,
                reversed[i],
                olayer.lamellaThickness / 2,
                olayer.lamellaLength,
                [offset / 200, j / beamSequence.length, 0],
                0,
                `Beam_${j}_${i}_${b}`,
                getColorFromString(beamSequence[j], beamColors)
              )
            );
            lamellas.push(
              createLamellaData(
                BeamComponent,
                reversed[i],
                olayer.lamellaThickness / 2,
                2,
                [offset / 200, j / beamSequence.length, olayer.lamellaLength / 4999],
                0,
                `Beam_${j}_${i + 100}_${b}`,
                getBeamColorFromLamellaSequence(k, getColorFromString(beamSequence[j], beamColors), true)
              )
            );

            const isLastItem = j === beamSequence.length - 1 && i === reversed.length - 1;
            if (renderQualityText && ((currentText !== previousText && previousText !== null) || isLastItem)) {
              const groupEndPosition = isLastItem ? (j + 1) / beamSequence.length : (j - 1 + i) / beamSequence.length;
              const textPosition = (groupStartPosition + groupEndPosition) / 2;
              const textColor = `#${getBeamColorFromLamellaSequence(k - 1, getColorFromString(beamSequence[j - 1], textColors), false)}`;

              lamellas.push({
                component: FiberText,
                position: [initialOffset / 200 + 0.3, textPosition, 3],
                children: previousText,
                size: 0.6,
                key: `text-${j}-${i}`,
                color: textColor
              });

              groupStartPosition = j / beamSequence.length;
            }

            previousText = currentText;
            k += 1;
            offset += reversed[i];
          }
        }
      });
    }

    return finalizeLamellas(lamellas);
  }

  // function computeLayerGap(lamellaHeight, layerCount) {
  //   const minGap = Number.isFinite(gapMinRaw) ? gapMinRaw : 0.01;
  //   const maxGap = Number.isFinite(gapMaxRaw) ? gapMaxRaw : 0.08;
  //   const factor = Number.isFinite(gapFactorRaw) ? gapFactorRaw : 0.6;
  //   const safeLamellaHeight = lamellaHeight > 0 ? lamellaHeight : minGap;
  //   const normalizedCount = Math.max(layerCount, 1);
  //   const dynamicGap = (safeLamellaHeight * factor) / Math.sqrt(normalizedCount);
  //   return Math.min(maxGap, Math.max(minGap, dynamicGap));
  // }

  function resolveLayerGap(lamellaHeight, layerCount) {
    if (!useDynamicGap && Number.isFinite(orderVerticalGapOverride)) {
      return orderVerticalGapOverride;
    }
    if (!useDynamicGap) {
      return NaN;
    }
    if (Number.isFinite(orderVerticalGapOverride)) {
      return orderVerticalGapOverride;
    }
    return 5;
  }

  /* Function to extract widths from beamWidth (convert mm to scene units) */
  function extractWidths(beamWidth) {
    return (beamWidth || [])
      .map((entry) => (typeof entry === 'object' && entry !== null ? entry.width ?? entry : entry))
      .map((val) => Number(val))
      .filter((val) => Number.isFinite(val) && val > 0)
      .map((val) => val / widthScaleDiv);
  }

  function getBeamColorFromLamellaSequence(index, defaultColor, checkFlags) {
    const lamellaSequence = _.get(order, 'lamellaSequence', null);
    let color = defaultColor;
    if (lamellaSequence != null) {
      const aLamella = lamellaSequence[index];

      if (checkFlags) {
        if (aLamella.emptied) {
          color = '5050ff';
        }
        if (aLamella.pressed) {
          color = '50ff50';
        }
      }
    }

    return color;
  }
  function resolveColor(index, colors) {
    return useBeamColors ? getColorFromString(index, colors) : getColorFromString(0, ['#808080']);
  }
  function rotateZ([x, y, z], angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return [x * cos - y * sin, x * sin + y * cos, z];
  }
  function rotateZWithPivot([x, y, z], angle, [px, py]) {
    const [rx, ry, rz] = rotateZ([x - px, y - py, z], angle);
    return [rx + px, ry + py, rz];
  }
  function getLayerRotationAngle(layerData) {
    if (!useRotation) {
      return 0;
    }
    const fallbackAngle = _.get(beamOptions, 'rotationAngle', -Math.PI / 2);
    const rawAngle = _.get(layerData, 'rotation', fallbackAngle);
    const angle = Number(rawAngle);
    if (!Number.isFinite(angle)) {
      return 0;
    }
    return Math.abs(angle) > 2 * Math.PI ? (angle * Math.PI) / 180 : angle;
  }

  function computeAutoBundleLayout(targetSize, itemCount, baseItemSize, baseGap = 0) {
    const count = Math.max(1, Number(itemCount) || 1);
    const safeBaseItem = Number.isFinite(baseItemSize) && baseItemSize > 0 ? baseItemSize : 0.0001;
    const safeBaseGap = Number.isFinite(baseGap) && baseGap > 0 ? baseGap : 0;
    const fallbackTotal = safeBaseItem * count + safeBaseGap * Math.max(count - 1, 0);
    const total = Number.isFinite(targetSize) && targetSize > 0 ? targetSize : fallbackTotal;

    const ratio = safeBaseItem > 0 ? Math.max(0, safeBaseGap / safeBaseItem) : 0;
    let itemSize = total / (count + Math.max(count - 1, 0) * ratio);
    if (!Number.isFinite(itemSize) || itemSize <= 0) {
      itemSize = total / count;
    }
    itemSize = Math.max(itemSize, 0.0001);

    const gap = count > 1 ? Math.max(total - itemSize * count, 0) / (count - 1) : 0;
    return { itemSize, gap };
  }

  function computePackedCenters(totalSpan, itemSpan, itemCount, gapSpan = 0) {
    const count = Math.max(1, Number(itemCount) || 1);
    const safeItemSpan = Number.isFinite(itemSpan) && itemSpan > 0 ? itemSpan : 0.0001;
    const safeGapSpan = Number.isFinite(gapSpan) && gapSpan > 0 ? gapSpan : 0;
    const resolvedTotal = Number.isFinite(totalSpan) && totalSpan > 0 ? totalSpan : safeItemSpan * count + safeGapSpan * Math.max(count - 1, 0);

    const centers = [];
    let cursor = -resolvedTotal / 2;
    for (let index = 0; index < count; index++) {
      const center = cursor + safeItemSpan / 2;
      centers.push(center);
      cursor += safeItemSpan + safeGapSpan;
    }
    return centers;
  }

  /* Selected layer */
  if (beamLayer) {
    const lamellaSequenceOverride = _.get(beamOptions, 'lamellaSequence', null);
    let beamSequence = Array.isArray(lamellaSequenceOverride) && lamellaSequenceOverride.length ? lamellaSequenceOverride : getBeamSequence(layer, beamBuild);
    const fixedLayerWidth = fixedLamellaWidth ?? null;
    let lamellaBuildRaw = fixedLayerWidth && fixedLayerWidth > 0 ? [fixedLayerWidth] : beamLayer.beamWidth && beamLayer.beamWidth.length ? beamLayer.beamWidth : [beamLayer.lamellaWidth];
    let lamellaBuild = extractWidths(lamellaBuildRaw || []);

    if (lamellaBuild.length === 0 && Number.isFinite(Number(beamLayer.lamellaWidth))) {
      lamellaBuild = [Number(fixedLayerWidth || beamLayer.lamellaWidth) / widthScaleDiv];
    }

    const seqLength = beamSequence.length || 1;
    const resolvedThicknessMm = Number(fixedLamellaThickness || beamLayer.lamellaThickness) || 0;
    const resolvedLengthMm = Number(fixedLamellaLength || beamLayer.lamellaLength) || 0;
    let thicknessScale = resolvedThicknessMm * thicknessScaleMultiplier;
    let lamellaHeight = thicknessScale / 10000 || 0.001;
    let dynamicWidth = lamellaBuild[0] || 1;
    let autoBundleGap = 0;

    const fallbackGap = Number.isFinite(orderVerticalGapOverride) ? orderVerticalGapOverride : 0.05;
    const normalizedStep = !isHorizontal && useNormalizedLayerSpacing && beamSequence.length ? 1 / beamSequence.length : 0;
    const computedLayerGap = resolveLayerGap(lamellaHeight, beamSequence.length) || fallbackGap;

    if (bundleSize !== null) {
      if (isHorizontal) {
        const bundleSizeScene = bundleSize / widthScaleDiv;
        const fixedBundleWidthScene = fixedLayerWidth && fixedLayerWidth > 0 ? fixedLayerWidth / widthScaleDiv : null;
        if (fixedBundleWidthScene && fixedBundleWidthScene > 0) {
          dynamicWidth = fixedBundleWidthScene;
          autoBundleGap = seqLength > 1 ? Math.max((bundleSizeScene - fixedBundleWidthScene * seqLength) / (seqLength - 1), 0) : 0;
        } else {
          const autoLayout = computeAutoBundleLayout(bundleSizeScene, seqLength, dynamicWidth, 0);
          dynamicWidth = Math.max(autoLayout.itemSize, 0.01);
          autoBundleGap = autoLayout.gap;
        }
      } else {
        const bundleSizeScene = (bundleSize * thicknessScaleMultiplier) / 10000;
        const autoLayout = computeAutoBundleLayout(bundleSizeScene, seqLength, lamellaHeight, 0);
        lamellaHeight = Math.max(autoLayout.itemSize, 0.0001);
        thicknessScale = lamellaHeight * 10000;
        autoBundleGap = 0;
      }
    }

    const totalWidth = isHorizontal ? seqLength * dynamicWidth + (bundleSize !== null ? Math.max(seqLength - 1, 0) * autoBundleGap : 0) : lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);
    const lengthScale = resolvedLengthMm * lengthScaleMultiplier;
    const rotationAngle = getLayerRotationAngle(beamLayer);
    const layerGap = isHorizontal ? (bundleSize !== null ? autoBundleGap : 0) : useNormalizedLayerSpacing && normalizedStep ? normalizedStep : bundleSize !== null ? autoBundleGap : computedLayerGap;

    if (lamellaBuild.length > 0) {
      let previousText = null;
      let groupStartHeight = 0;
      let stackHeight = 0;
      const initialOffset = -totalWidth / 2;
      const baseTextX = initialOffset / 200 + 0.3 + textXOffset;
      let textIndex = 0;
      const layerPivot = isHorizontal ? [0, 0] : [0, useNormalizedLayerSpacing ? (beamSequence.length * normalizedStep) / 2 : (beamSequence.length * (lamellaHeight + layerGap)) / 2];
      for (let j = 0; j < beamSequence.length; j++) {
        const currentText = getQualityText(beamSequence[j], beamBuildText);
        const isPendingLamella = (pendingSequence && pendingSequence[j] === true) || String(beamSequence[j] ?? '').toLowerCase() === 'pending';
        const pendingProps = isPendingLamella
          ? {
              desaturate: true,
              saturationMultiplier: 0.28,
              pendingSolid: true,
              pendingSolidColor: pendingSolidColorOverride || (Array.isArray(beamColors) && beamColors[0]) || '#808080',
              pendingOpacity: pendingOpacityOverride
            }
          : null;

        if (isHorizontal) {
          const lamellaWidthWorld = dynamicWidth / 100;
          const layerGapWorld = layerGap / 100;
          const totalBundleWidthWorld = beamSequence.length * lamellaWidthWorld + Math.max(beamSequence.length - 1, 0) * layerGapWorld;
          const horizontalCenters = computePackedCenters(totalBundleWidthWorld, lamellaWidthWorld, beamSequence.length, layerGapWorld);
          const xCenter = horizontalCenters[j] ?? 0;
          const lamellaPosition = rotateZWithPivot([xCenter, 0, 0], rotationAngle, layerPivot);
          lamellas.push(
            createLamellaData(
              BeamComponent,
              dynamicWidth,
              thicknessScale,
              lengthScale,
              lamellaPosition,
              [0, 0, rotationAngle],
              `WoodOutlined_${j}_0`,
              resolveColor(beamSequence[j], beamColors),
              pendingProps
            )
          );

          if (renderQualityText && (currentText !== previousText || j === beamSequence.length - 1)) {
            lamellas.push({
              component: FiberText,
              position: rotateZWithPivot([xCenter + textXOffset + textIndex * textXStep, textYOffset + textIndex * textYStep, 3 + textIndex * textZStep + textZOffset], rotationAngle, layerPivot),
              children: currentText,
              size: 0.6,
              key: `text-horizontal-${j}`,
              color: `#${getBeamColorFromLamellaSequence(j, resolveColor(beamSequence[j], beamColors), false)}`
            });
            textIndex += 1;
          }

          previousText = currentText;
          continue;
        }

        let offset = initialOffset;
        let reversed = (j + 1) % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
        const layerStart = useNormalizedLayerSpacing ? normalizedStep * j : stackHeight;
        const layerEnd = useNormalizedLayerSpacing ? normalizedStep * (j + 1) : stackHeight + lamellaHeight + layerGap;
        const layerCenter = useNormalizedLayerSpacing ? normalizedStep * j : (layerStart + layerEnd) / 2;

        for (let i = 0; i < reversed.length; i++) {
          const width = reversed[i];
          const centerX = offset + width / 2;
          const lamellaPosition = rotateZWithPivot([centerX / 200, layerCenter, 0], rotationAngle, layerPivot);
          lamellas.push(
            createLamellaData(
              BeamComponent,
              width,
              thicknessScale,
              lengthScale,
              lamellaPosition,
              [0, 0, rotationAngle],
              `WoodOutlined_${j}_${i}`,
              resolveColor(beamSequence[j], beamColors),
              pendingProps
            )
          );
          offset += width;
        }

        const isLastLayer = j === beamSequence.length - 1;
        if (renderQualityText && currentText !== previousText && previousText != null) {
          const textPosition = (groupStartHeight + layerStart) / 2 + textIndex * textYStep + textYOffset;
          lamellas.push({
            component: FiberText,
            position: rotateZWithPivot([baseTextX + textIndex * textXStep, textPosition, 3 + textIndex * textZStep + textZOffset], rotationAngle, layerPivot),
            children: previousText,
            size: 0.6,
            key: `text-${j}-prev`,
            color: `#${getBeamColorFromLamellaSequence(j - 1, resolveColor(beamSequence[j - 1], beamColors), false)}`
          });
          groupStartHeight = layerStart;
          textIndex += 1;
        }

        if (renderQualityText && isLastLayer) {
          const textPosition = (groupStartHeight + layerEnd) / 2 + textIndex * textYStep + textYOffset;
          lamellas.push({
            component: FiberText,
            position: rotateZWithPivot([baseTextX + textIndex * textXStep, textPosition, 3 + textIndex * textZStep + textZOffset], rotationAngle, layerPivot),
            children: currentText,
            size: 0.6,
            key: `text-${j}-last`,
            color: `#${getBeamColorFromLamellaSequence(j, resolveColor(beamSequence[j], beamColors), false)}`
          });
          textIndex += 1;
        }

        previousText = currentText;
        stackHeight = layerEnd;
      }
    }
  } else {
    /* Order */
    let k = 0;
    const spacingMultiplier = orderWidthSpacing;
    let globalTextIndex = 0;

    const targetLayers = order?.layers || order?.bshLayers || [];
    targetLayers.forEach((olayer, b) => {
      const beamSequence = getBeamSequence(olayer, beamBuild);
      const fixedLayerWidth = fixedLamellaWidth ?? null;
      let lamellaBuild = extractWidths(fixedLayerWidth && fixedLayerWidth > 0 ? [fixedLayerWidth] : olayer.beamWidth || []);

      if (lamellaBuild.length === 0 && Number.isFinite(Number(olayer.lamellaWidth))) {
        lamellaBuild = [Number(fixedLayerWidth || olayer.lamellaWidth) / widthScaleDiv];
      }

      const totalWidth = lamellaBuild.reduce((partialSum, w) => partialSum + w, 0);
      const resolvedThicknessMm = Number(fixedLamellaThickness || olayer.lamellaThickness) || 0;
      const resolvedLengthMm = Number(fixedLamellaLength || olayer.lamellaLength) || 0;
      const thicknessScale = resolvedThicknessMm * thicknessScaleMultiplier;
      const lamellaHeight = thicknessScale / 10000 || 0.001;
      const lengthScale = resolvedLengthMm * lengthScaleMultiplier;
      const rotationAngle = getLayerRotationAngle(olayer);
      const fallbackGap = Number.isFinite(orderVerticalGapOverride) ? orderVerticalGapOverride : 0.05;
      const normalizedStep = useNormalizedLayerSpacing && beamSequence.length ? 1 / beamSequence.length : 0;
      const computedLayerGap = resolveLayerGap(lamellaHeight, beamSequence.length) || fallbackGap;
      const layerGap = useNormalizedLayerSpacing && normalizedStep ? normalizedStep : computedLayerGap;

      if (!lamellaBuild.length) {
        return;
      }

      let previousText = null;
      let groupStartHeight = 0;
      let stackHeight = 0;
      const baseInitialOffset = isLegacyLayout ? (-totalWidth / 2) * spacingMultiplier * (b + 1) : -totalWidth / 2;
      const layerShift = isLegacyLayout ? 0 : spacingMultiplier * b;
      const layerPivot = [layerShift / 200, useNormalizedLayerSpacing ? (beamSequence.length * normalizedStep) / 2 : (beamSequence.length * (lamellaHeight + layerGap)) / 2];
      const textBaseX = (baseInitialOffset + layerShift) / 200 + 0.3 + textXOffset;

      for (let j = 0; j < beamSequence.length; j++) {
        let reversed = j % 2 === 1 ? lamellaBuild.slice().reverse() : lamellaBuild;
        let offset = baseInitialOffset;
        const layerStart = useNormalizedLayerSpacing ? normalizedStep * j : stackHeight;
        const layerEnd = useNormalizedLayerSpacing ? normalizedStep * (j + 1) : stackHeight + lamellaHeight + layerGap;
        const layerCenter = useNormalizedLayerSpacing ? normalizedStep * j : (layerStart + layerEnd) / 2;
        const currentText = getQualityText(beamSequence[j], beamBuildText);

        for (let i = 0; i < reversed.length; i++) {
          const width = reversed[i];
          const centerX = offset + width / 2 + layerShift;
          const lamellaPosition = rotateZWithPivot([centerX / 200, layerCenter, 0], rotationAngle, layerPivot);
          lamellas.push(createLamellaData(BeamComponent, width, thicknessScale, lengthScale, lamellaPosition, [0, 0, rotationAngle], `Beam_${j}_${i}_${b}`, resolveColor(beamSequence[j], beamColors)));
          const lamellaPositionRotated = rotateZWithPivot([centerX / 200, layerCenter, lengthScale / 4999], rotationAngle, layerPivot);
          lamellas.push(
            createLamellaData(
              BeamComponent,
              width,
              thicknessScale,
              2,
              lamellaPositionRotated,
              [0, 0, rotationAngle],
              `Beam_${j}_${i + 100}_${b}`,
              getBeamColorFromLamellaSequence(k, resolveColor(beamSequence[j], beamColors), true)
            )
          );

          k += 1;
          offset += width;
        }

        const isLastLayer = j === beamSequence.length - 1;
        if (renderQualityText && currentText !== previousText && previousText !== null) {
          const textPosition = (groupStartHeight + layerStart) / 2 + globalTextIndex * textYStep + textYOffset;
          const textColor = `#${getBeamColorFromLamellaSequence(k - 1, resolveColor(beamSequence[j - 1], textColors), false)}`;

          lamellas.push({
            component: FiberText,
            position: rotateZWithPivot([textBaseX + globalTextIndex * textXStep, textPosition, 3 + globalTextIndex * textZStep + textZOffset], rotationAngle, layerPivot),
            children: previousText,
            size: 0.6,
            key: `text-${j}-prev-${b}`,
            color: textColor
          });

          groupStartHeight = layerStart;
          globalTextIndex += 1;
        }

        if (renderQualityText && isLastLayer) {
          const textPosition = (groupStartHeight + layerEnd) / 2 + globalTextIndex * textYStep + textYOffset;
          const textColor = `#${getBeamColorFromLamellaSequence(k - 1, resolveColor(beamSequence[j], textColors), false)}`;

          lamellas.push({
            component: FiberText,
            position: rotateZWithPivot([textBaseX + globalTextIndex * textXStep, textPosition, 3 + globalTextIndex * textZStep + textZOffset], rotationAngle, layerPivot),
            children: currentText,
            size: 0.6,
            key: `text-${j}-last-${b}`,
            color: textColor
          });
          globalTextIndex += 1;
        }

        previousText = currentText;
        stackHeight = layerEnd;
      }
      // Handle the last group of lamellas
    });
  }

  return finalizeLamellas(lamellas);
}
